<template>
  <Teleport to="body">
    <Transition name="dialog-fade">
      <div v-if="open" class="dialog-overlay" @mousedown.self="cancel">
        <section ref="cardRef" class="dialog-card" role="dialog" aria-modal="true" :aria-labelledby="titleId" tabindex="-1" @keydown="onKeydown">
          <div class="dialog-icon" :class="{ danger }" aria-hidden="true">
            <Trash2 v-if="danger" :size="22" />
            <FolderPlus v-else :size="22" />
          </div>
          <button type="button" class="dialog-close" aria-label="关闭" @click="cancel">×</button>
          <div class="dialog-content">
            <h3 :id="titleId">{{ title }}</h3>
            <p v-if="message">{{ message }}</p>
            <label v-if="input" class="dialog-field" :class="{ 'has-error': errorMessage }">
              <span>{{ inputLabel }}</span>
              <input
                ref="inputRef"
                :value="inputValue"
                :placeholder="inputPlaceholder"
                maxlength="40"
                :aria-invalid="errorMessage ? 'true' : 'false'"
                @input="$emit('update:inputValue', $event.target.value)"
                @keydown.enter="confirm"
                @keydown.esc="cancel"
              />
              <p v-if="errorMessage" class="dialog-error" role="alert">{{ errorMessage }}</p>
            </label>
          </div>
          <footer class="dialog-actions">
            <button type="button" class="dialog-btn" @click="cancel">取消</button>
            <button
              type="button"
              class="dialog-btn primary"
              :class="{ danger }"
              :disabled="input && !inputValue.trim()"
              @click="confirm"
            >{{ confirmText }}</button>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue';
import { FolderPlus, Trash2 } from '@lucide/vue';

const props = defineProps({
  open: Boolean,
  title: { type: String, default: '确认操作' },
  message: { type: String, default: '' },
  confirmText: { type: String, default: '确认' },
  danger: Boolean,
  input: Boolean,
  inputLabel: { type: String, default: '名称' },
  inputValue: { type: String, default: '' },
  inputPlaceholder: { type: String, default: '' },
  errorMessage: { type: String, default: '' },
});

const emit = defineEmits(['confirm', 'cancel', 'update:inputValue']);
const inputRef = ref(null);
const cardRef = ref(null);
const titleId = `system-dialog-${Math.random().toString(36).slice(2, 8)}`;
let previousFocus = null;

watch(() => props.open, async (open) => {
  if (!open) {
    previousFocus?.focus?.();
    return;
  }
  previousFocus = document.activeElement;
  await nextTick();
  (inputRef.value || cardRef.value)?.focus();
  inputRef.value?.select();
});

function onKeydown(event) {
  if (event.key === 'Escape') {
    event.preventDefault();
    cancel();
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = [...cardRef.value.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function cancel() {
  emit('cancel');
}

function confirm() {
  if (props.input && !props.inputValue.trim()) return;
  emit('confirm');
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed; inset: 0; z-index: 12000;
  display: grid; place-items: center; padding: 20px;
  background: rgba(15, 23, 42, 0.46);
  backdrop-filter: blur(3px);
}
.dialog-card {
  position: relative; width: min(420px, 100%);
  padding: 28px 28px 22px; border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 18px; background: #fff;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.24);
}
.dialog-icon {
  width: 46px; height: 46px; display: grid; place-items: center;
  margin-bottom: 18px; border-radius: 13px;
  color: var(--color-info-text); background: var(--color-info-light); border: 1px solid var(--color-info-border);
}
.dialog-icon.danger { color: #dc2626; background: #fff1f2; border-color: #fecdd3; }
.dialog-close {
  position: absolute; top: 16px; right: 16px;
  width: 32px; height: 32px; border: 0; border-radius: 9px;
  background: #f8fafc; color: #64748b; font-size: 20px; line-height: 1; cursor: pointer;
}
.dialog-close:hover { color: #0f172a; background: #f1f5f9; }
.dialog-content h3 { margin: 0; color: #0f172a; font-size: 19px; font-weight: 750; }
.dialog-content p { margin: 9px 0 0; color: #64748b; font-size: 13px; line-height: 1.65; }
.dialog-field { display: grid; gap: 7px; margin-top: 18px; }
.dialog-field span { color: #475569; font-size: 12px; font-weight: 600; }
.dialog-field input {
  width: 100%; height: 42px; padding: 0 12px; box-sizing: border-box;
  border: 1px solid #dbe3ed; border-radius: 10px; outline: none;
  color: #0f172a; background: #fff; font-size: 14px;
}
.dialog-field input:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12); }
.dialog-field.has-error input {
  border-color: #f87171;
  background: #fffafb;
}
.dialog-field.has-error input:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
}
.dialog-error {
  margin: 0;
  color: #dc2626;
  font-size: 12px;
  line-height: 1.5;
}
.dialog-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }
.dialog-btn {
  min-width: 86px; height: 38px; padding: 0 17px;
  border: 1px solid #dbe3ed; border-radius: 999px;
  color: #334155; background: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
}
.dialog-btn:hover { background: #f8fafc; }
.dialog-btn.primary { border-color: #7c3aed; color: #fff; background: #7c3aed; }
.dialog-btn.primary:hover { background: #0b665f; }
.dialog-btn.primary.danger { border-color: #dc2626; background: #dc2626; }
.dialog-btn.primary.danger:hover { background: #b91c1c; }
.dialog-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.dialog-fade-enter-active, .dialog-fade-leave-active { transition: opacity 0.18s ease; }
.dialog-fade-enter-active .dialog-card, .dialog-fade-leave-active .dialog-card { transition: transform 0.18s ease; }
.dialog-fade-enter-from, .dialog-fade-leave-to { opacity: 0; }
.dialog-fade-enter-from .dialog-card, .dialog-fade-leave-to .dialog-card { transform: translateY(8px) scale(0.98); }
</style>
